import { apiClient } from './client';
import {
  Department,
  EmployeeCard,
  EmployeeCardPayload,
  EmployeeHistoryItem,
  OrgMemberSummary,
} from '../types/org';

export async function fetchDepartments(): Promise<Department[]> {
  const { data } = await apiClient.get<Department[]>('/org/departments');
  return data;
}

export async function createDepartment(departmentName: string): Promise<Department> {
  const { data } = await apiClient.post<Department>('/org/departments', { departmentName });
  return data;
}

export async function updateDepartment(
  departmentId: number,
  payload: { departmentName?: string; sortOrder?: number }
): Promise<Department> {
  const { data } = await apiClient.patch<Department>(`/org/departments/${departmentId}`, payload);
  return data;
}

export async function deleteDepartment(departmentId: number): Promise<void> {
  await apiClient.delete(`/org/departments/${departmentId}`);
}

export async function fetchOrgMembers(params?: {
  departmentId?: number | null;
  unassigned?: boolean;
  q?: string;
  excludeSelf?: boolean;
}): Promise<OrgMemberSummary[]> {
  const query: Record<string, string | number | boolean> = {};
  if (params?.departmentId != null) query.departmentId = params.departmentId;
  if (params?.unassigned) query.unassigned = true;
  if (params?.q) query.q = params.q;
  if (params?.excludeSelf) query.excludeSelf = true;
  const { data } = await apiClient.get<OrgMemberSummary[]>('/org/members', { params: query });
  return data;
}

export async function assignMemberDepartment(
  userId: number,
  departmentId: number | null
): Promise<OrgMemberSummary> {
  const { data } = await apiClient.patch<OrgMemberSummary>(`/org/members/${userId}/department`, {
    departmentId,
  });
  return data;
}

export async function fetchEmployeeCard(userId: number): Promise<EmployeeCard> {
  const { data } = await apiClient.get<EmployeeCard>(`/org/members/${userId}`);
  return data;
}

export async function fetchMyEmployeeCard(): Promise<EmployeeCard> {
  const { data } = await apiClient.get<EmployeeCard>('/org/members/me');
  return data;
}

export async function updateEmployeeCard(
  userId: number,
  payload: EmployeeCardPayload
): Promise<EmployeeCard> {
  const { data } = await apiClient.put<EmployeeCard>(`/org/members/${userId}`, payload);
  return data;
}

export async function deactivateMember(userId: number): Promise<void> {
  await apiClient.post(`/org/members/${userId}/deactivate`);
}

export async function uploadEmployeePhoto(userId: number, image: File): Promise<EmployeeCard> {
  const formData = new FormData();
  formData.append('image', image);
  const { data } = await apiClient.post<EmployeeCard>(`/org/members/${userId}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function deleteEmployeePhoto(userId: number): Promise<void> {
  await apiClient.delete(`/org/members/${userId}/photo`);
}

export async function fetchEmployeePhotoUrl(userId: number): Promise<string> {
  const { data } = await apiClient.get<Blob>(`/org/members/${userId}/photo`, {
    responseType: 'blob',
  });
  return window.URL.createObjectURL(data);
}

export async function fetchMyEmployeePhotoUrl(): Promise<string> {
  const { data } = await apiClient.get<Blob>('/org/members/me/photo', {
    responseType: 'blob',
  });
  return window.URL.createObjectURL(data);
}

export async function fetchEmployeeHistory(
  userId: number,
  type?: string
): Promise<EmployeeHistoryItem[]> {
  const { data } = await apiClient.get<EmployeeHistoryItem[]>(`/org/members/${userId}/history`, {
    params: type && type !== 'ALL' ? { type } : {},
  });
  return data;
}

export async function fetchMyEmployeeHistory(type?: string): Promise<EmployeeHistoryItem[]> {
  const { data } = await apiClient.get<EmployeeHistoryItem[]>('/org/members/me/history', {
    params: type && type !== 'ALL' ? { type } : {},
  });
  return data;
}

export interface EmployeeNoSuggestion {
  lastValue?: string | null;
  suggestedValue?: string | null;
}

export async function suggestEmployeeNo(): Promise<EmployeeNoSuggestion> {
  const { data } = await apiClient.get<EmployeeNoSuggestion>('/org/suggestions/employee-no');
  return data;
}
